// Mental health
import { GAD7 } from './mentalHealth/gad7';
import { PHQ9 } from './mentalHealth/phq9';
import { BIG_FIVE } from './mentalHealth/bigFive';
import { ECR_R } from './mentalHealth/ecrR';
import { RSES } from './mentalHealth/rses';
import { BRS } from './mentalHealth/brs';
import { PSS10 } from './mentalHealth/pss10';
import { ACE } from './mentalHealth/aceScore';
import { PCL5 } from './mentalHealth/pcl5';
import { PG13 } from './mentalHealth/pg13';
import { CECA_Q } from './mentalHealth/cecaQ';
import { SOCIAL_PRESSURE } from './mentalHealth/socialPressure';
import { RELIGIOUS_CULTURAL } from './mentalHealth/religiousCultural';
import { ECONOMIC_STRESS } from './mentalHealth/economicStress';

// Bonus
import {
  BONUS_NARCISSISME, BONUS_PERSONNALITE, BONUS_DEPENDANCE, BONUS_HSP,
  BONUS_HPI, BONUS_TDAH, BONUS_MANIPULATION, BONUS_BURNOUT,
  BONUS_JALOUSIE, BONUS_EQ, BONUS_CONFIANCE, BONUS_SOMMEIL,
} from './bonus';

// Sexual health
import { NSSS } from './sexualHealth/nsss';
import { SDI2 } from './sexualHealth/sdi2';
import { SIS_SES } from './sexualHealth/sisSes';
import { FSFI } from './sexualHealth/fsfi';
import { IIEF } from './sexualHealth/iief';
import { TSI_BASE } from './sexualHealth/tsiBase';
import { PAIR } from './sexualHealth/pair';
import { SISE } from './sexualHealth/sise';
import { SOCIAL_PRESSURE_SEX } from './sexualHealth/socialPressureSex';
import { GRISS_BASE } from './sexualHealth/grissBase';

import type { AssessmentScale } from '../../types/assessment';
import type { SexualExperienceProfile } from '../../types/onboarding';
import { getAdaptedScale } from '../../utils/sexualHealthFilter';

export const MENTAL_HEALTH_SCALES: AssessmentScale[] = [
  GAD7, PHQ9, BIG_FIVE, ECR_R, RSES, BRS, PSS10, ACE, PCL5, PG13,
  CECA_Q, SOCIAL_PRESSURE, RELIGIOUS_CULTURAL, ECONOMIC_STRESS,
];

export const SEXUAL_HEALTH_SCALES: AssessmentScale[] = [
  NSSS, SDI2, SIS_SES, FSFI, IIEF, TSI_BASE, PAIR, SISE,
  SOCIAL_PRESSURE_SEX, GRISS_BASE,
];

export const BONUS_SCALES: AssessmentScale[] = [
  // Phase 1 — Les 6 plus viraux
  BONUS_DEPENDANCE, BONUS_NARCISSISME, BONUS_HSP, BONUS_TDAH, BONUS_HPI, BONUS_CONFIANCE,
  // Phase 2 — Les 5 suivants
  BONUS_PERSONNALITE, BONUS_MANIPULATION, BONUS_BURNOUT, BONUS_JALOUSIE, BONUS_EQ,
  // Phase 3 — Sommeil (porte d'entrée du programme Sommeil)
  BONUS_SOMMEIL,
];

export const ALL_SCALES: AssessmentScale[] = [
  ...MENTAL_HEALTH_SCALES,
  ...SEXUAL_HEALTH_SCALES,
  ...BONUS_SCALES,
];

// Guard: every scale must have a unique, non-empty code
(function assertUniqueCodes() {
  const seen = new Set<string>();
  for (const s of ALL_SCALES) {
    if (!s.code) throw new Error(`Scale "${s.id}" has no code`);
    if (seen.has(s.code)) throw new Error(`Duplicate scale code "${s.code}" on "${s.id}"`);
    seen.add(s.code);
  }
})();

export function getScaleById(id: string): AssessmentScale | undefined {
  return ALL_SCALES.find(s => s.id === id);
}

export function getScaleByCode(code: string): AssessmentScale | undefined {
  const upper = code.toUpperCase();
  return ALL_SCALES.find(s => s.code === upper);
}

export function getBonusScaleById(id: string): AssessmentScale | undefined {
  return BONUS_SCALES.find(s => s.id === id);
}

export function getAdaptedScaleById(
  id: string,
  sexualProfile?: SexualExperienceProfile
): AssessmentScale | undefined {
  const scale = ALL_SCALES.find(s => s.id === id);
  if (!scale || !sexualProfile) return scale;
  return getAdaptedScale(scale, sexualProfile);
}
